import { IUser } from "./IUser";
import { CorePermission } from "./CorePermission";
import { PartnerTier, PartnerTierType } from "./IPartnerInfo";

export interface IPermissionInfo {
  readableName?: string;
  description?: string;
}

export interface IPermissionExtended extends IPermissionInfo {
  permission: CorePermission;
}

// Additional permission info (for UI) in a separate map
export const permissionInfo = new Map<CorePermission, IPermissionInfo>([
  [
    CorePermission.login,
    {
      readableName: "Login to iLOQ Partner Portal",
      description: "Can login to iLOQ Partner Portal",
    },
  ],
  [
    CorePermission.order,
    {
      readableName: "Order",
      description: "Can order products and see prices",
    },
  ],
  [
    CorePermission.academy,
    {
      readableName: "Academy",
      description: "Can access Academy",
    },
  ],
  [
    CorePermission.licenses,
    {
      readableName: "Licenses",
      description: "Can access Licenses",
    },
  ],
  [
    CorePermission.returns,
    {
      readableName: "Returns",
      description: "Can access Returns",
    },
  ],
  [
    CorePermission.brandstore,
    {
      readableName: "Brandstore",
      description: "Can access Brandstore",
    },
  ],
  [
    CorePermission.partnerProgram,
    {
      readableName: "Partner Program",
      description: "Can access Partner Program",
    },
  ],
  [
    CorePermission.partnerProgramConsumer,
    {
      readableName: "Partner Program Consumer",
      description: "Can access Partner Program, resend surveys and spend points",
    },
  ],
  [
    CorePermission.partnerProgramRestrictedData,
    {
      readableName: "Renewed Partner Program",
      description: "Can access the renewed Partner Program",
    },
  ],
  [
    CorePermission.partnerProgramCreateOpportunity,
    {
      readableName: "Partner Program Lead Generation",
      description: "Can create new opportunity projects in the Partner Program",
    },
  ],
  [
    CorePermission.viewUserManagement,
    {
      readableName: "View User Management",
      description: "Can view user account list",
    },
  ],
  [
    CorePermission.userManagement,
    {
      readableName: "User Management",
      description: "Can manage user accounts",
    },
  ],
  [
    CorePermission.distributorUserManagement,
    {
      readableName: "User management for distributors",
      description: "Can manage distributor users",
    },
  ],
  [
    CorePermission.partnerInfoManagement,
    {
      readableName: "Partner Information Management",
      description: "Can manage partner information",
    },
  ],
]);

export function getReadableName(permission: CorePermission): string {
  const info = permissionInfo.get(permission);
  return info?.readableName ?? permission;
}

export function getPermissionDescription(permission: CorePermission): string | undefined {
  const info = permissionInfo.get(permission);
  return info?.description;
}

export function listExtendedPermissions(): IPermissionExtended[] {
  return Object.values(CorePermission).map((permission: CorePermission) => ({
    permission,
    readablePermission: `${permission}`,
    readableName: getReadableName(permission),
    description: getPermissionDescription(permission),
  }));
}

const permissionsByRoleGroup: { [key: string]: CorePermission[] } = {
  Role_Partner: [
    CorePermission.login,
    CorePermission.order,
    CorePermission.academy,
    CorePermission.licenses,
    CorePermission.returns,
    CorePermission.brandstore,
    CorePermission.partnerProgram, // Remove this after old Partner Program sunset
    CorePermission.partnerInfoManagement,
  ],
  Role_Architect: [
    CorePermission.login,
    CorePermission.order,
    CorePermission.academy,
    CorePermission.licenses,
    CorePermission.returns,
    CorePermission.brandstore,
  ],
  Role_Installer: [
    CorePermission.login,
    CorePermission.academy,
    CorePermission.licenses,
    CorePermission.returns,
    CorePermission.brandstore,
  ],
  Role_iLOQ_Sales: [
    CorePermission.login,
    CorePermission.order,
    CorePermission.licenses,
    CorePermission.returns,
    CorePermission.brandstore,
  ],
  Role_Basic_User: [CorePermission.login, CorePermission.academy],
  Role_Distributor: [CorePermission.login, CorePermission.academy, CorePermission.brandstore],
  Role_Distributor_Admin: [
    CorePermission.login,
    CorePermission.academy,
    CorePermission.brandstore,
    CorePermission.partnerInfoManagement,
    CorePermission.viewUserManagement,
    CorePermission.distributorUserManagement,
  ],
  Role_Admin: [
    CorePermission.login,
    CorePermission.order,
    CorePermission.academy,
    CorePermission.licenses,
    CorePermission.returns,
    CorePermission.brandstore,
    CorePermission.partnerProgram, // Remove this after old Partner Program sunset
    CorePermission.partnerProgramConsumer, // Remove this after old Partner Program sunset
    CorePermission.viewUserManagement,
    CorePermission.userManagement,
    CorePermission.partnerInfoManagement,
  ],
  Role_Partner_Program_User: [
    CorePermission.partnerProgram,
    CorePermission.partnerProgramRestrictedData,
    CorePermission.partnerProgramCreateOpportunity,
  ],
};

const partnerProgramPermissions = [
  CorePermission.partnerProgram,
  CorePermission.partnerProgramConsumer,
  CorePermission.partnerProgramRestrictedData,
  CorePermission.partnerProgramCreateOpportunity,
];

const activePartnerTiers = [
  PartnerTier.PLATINUM,
  PartnerTier.GOLD,
  PartnerTier.AUTHORIZED,
] as PartnerTierType[];

function isPartnerProgramRestrictedDataEnabled(permissions: CorePermission[]) {
  return permissions.some(
    (permission) => permission === CorePermission.partnerProgramRestrictedData,
  );
}

function removePartnerProgramPermissions(permissions: CorePermission[]): CorePermission[] {
  return permissions.filter((permission) => !partnerProgramPermissions.includes(permission));
}

function restrictLegacyPartnerProgramByCountry({
  countryCode,
  countriesAllowedUsePartnerProgram,
  permissions,
}: {
  countryCode: string;
  countriesAllowedUsePartnerProgram?: string[] | null | undefined;
  permissions: CorePermission[];
}): CorePermission[] {
  /* Partner program not allowed in a country at all */
  if (!countriesAllowedUsePartnerProgram?.includes(countryCode)) {
    return removePartnerProgramPermissions(permissions);
  }

  return permissions;
}

function restrictLeadGenerationByCountry({
  countryCode,
  countriesDisabledLeadGeneration,
  permissions,
}: {
  countryCode: string;
  countriesDisabledLeadGeneration: string[] | null | undefined;
  permissions: CorePermission[];
}): CorePermission[] {
  /* Partner program opportunity creation not allowed in a country */
  if (countriesDisabledLeadGeneration?.includes(countryCode)) {
    return permissions.filter(
      (permission) => permission !== CorePermission.partnerProgramCreateOpportunity,
    );
  }

  return permissions;
}

export function fetchExtendedPermissionsForUser({
  user,
  tier,
  countriesAllowedUsePartnerProgram,
  countriesDisabledLeadGeneration,
}: {
  user: IUser;
  tier: PartnerTierType;
  countriesAllowedUsePartnerProgram: string[] | null | undefined;
  countriesDisabledLeadGeneration: string[] | null | undefined;
}): IPermissionExtended[] {
  let userPermissions: CorePermission[] = [];
  const { countryCode } = user;

  user.groups.forEach((group) => {
    userPermissions.push(...(permissionsByRoleGroup[group] || []));
  });

  // Remove the if-else below after old Partner Program sunset. Consider if "remove partner program permissions
  // if not in active tier" safety check should be added then (in theory it's possible that a user has the
  // Role_Partner_Program_User group even when the partner is not in active tier -- case1: partner has had tier,
  // but it has been removed; case2: group has been added manually via Azure portal)
  if (activePartnerTiers.includes(tier)) {
    if (!isPartnerProgramRestrictedDataEnabled(userPermissions)) {
      userPermissions = removePartnerProgramPermissions(userPermissions);
    } else {
      userPermissions = restrictLeadGenerationByCountry({
        countryCode,
        countriesDisabledLeadGeneration,
        permissions: userPermissions,
      });
    }
  } else {
    userPermissions = restrictLegacyPartnerProgramByCountry({
      countryCode,
      countriesAllowedUsePartnerProgram,
      permissions: userPermissions,
    });
    userPermissions = userPermissions.filter(
      (permission) =>
        permission !== CorePermission.partnerProgramRestrictedData &&
        permission !== CorePermission.partnerProgramCreateOpportunity,
    );
  }

  return listExtendedPermissions().filter((permission: IPermissionExtended) =>
    userPermissions.includes(permission.permission),
  );
}
